import React from "react";
import { Link } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import * as c from "./lcArticleBody.module.scss";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import LcArticleCalloutBlock from "@components/learningCenter/lcArticleCalloutBlock";
import WistiaVideoEmbed from "@components/wistia/wistiaVideoEmbed";
import CtaButton from "@components/shared/cta/ctaButton";
import LcArticleBlock from "@components/learningCenter/lcArticleBlock";
import { Faqs } from "@components/shared";
import { createRichTextOptions } from "@components/shared/richTextOptions";

export default function LcArticleBody({ body, appendBlock, noAnchorLinks = false }) {
  const options = {
    renderNode: {
      ...createRichTextOptions({ noAnchorLinks }).renderNode,
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const type = node?.data?.target?.__typename;

        switch (type) {
          case "ContentfulLcArticleCallout": {
            const calloutType = node.data.target?.calloutType;
            const richTextDescription = node.data.target?.richTextDescription;

            return <LcArticleCalloutBlock calloutType={calloutType} richTextDescription={richTextDescription} />;
          }
          case "ContentfulLcArticleHtmlEmbed": {
            const htmlEmbed = node.data.target?.html?.html;

            return <div className={c.htmlEmbed} dangerouslySetInnerHTML={{ __html: htmlEmbed }} />;
          }
          case "ContentfulLcArticleWistiaVideoEmbed": {
            const wistiaVideoId = node.data.target?.wistiaVideoId;
            const trackedEventName = node.data.target?.trackedEventName;

            return (
              <WistiaVideoEmbed
                className={c.wistiaVideoEmbed}
                wistiaVideoId={wistiaVideoId}
                trackedEventName={trackedEventName}
              />
            );
          }
          case "ContentfulCtaButton": {
            const cta = node.data.target;
            const ctaHasButtonStyle = ({ style }) =>
              ["Button Primary", "Button Secondary"].some((s) => style.includes(s));

            // Ideally should find a way to validate this in Contentful, but it's not supported OOTB
            if (!ctaHasButtonStyle(cta)) {
              console.warn(`Unsupported CTA Button style for CTA with label: ${cta?.label}`);
              return null;
            }

            return <CtaButton className={c.buttonPrimary} {...node.data.target} />;
          }
          case "ContentfulGenericList": {
            const faqs = node.data.target;

            return <Faqs className={c.faqsSection} heading={faqs.heading} faqs={faqs.listItems} />;
          }
          case undefined: {
            console.warn(`Undefined __typename on EMBEDDED_ENTRY`);
            return null;
          }
          default: {
            console.warn(`Unsupported embedded entry type: ${type}`);
            return null;
          }
        }
      },

      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const asset = node.data.target;
        let Component;

        if (asset?.file?.contentType === "video/mp4") {
          Component = (
            <div className={c.videoCtn}>
              <video autoPlay={true} muted={true} loop={true} playsInline={true}>
                <source src={asset.file?.url} />
              </video>
            </div>
          );
        } else if (asset?.gatsbyImageData) {
          Component = (
            <div className={c.imgCtn}>
              <GatsbyImage image={asset.gatsbyImageData} alt="" />
            </div>
          );
        }
        return Component;
      },

      [INLINES.EMBEDDED_ENTRY]: (node) => {
        const type = node?.data?.target?.__typename;

        switch (type) {
          case "ContentfulLcCategory":
          case "ContentfulLcSubcategory":
          case "ContentfulLcArticle": {
            return <LcArticleBlock className={c.articleBlock} node={node} />;
          }
          case "ContentfulLcArticleHtmlEmbed": {
            const htmlEmbed = node.data.target?.html?.html;
            return <div className={c.headingEmbed} dangerouslySetInnerHTML={{ __html: htmlEmbed }} />;
          }
          case undefined: {
            console.warn(`Undefined __typename on INLINES.EMBEDDED_ENTRY`);
            return null;
          }
          default: {
            console.warn(`Unsupported inline embedded entry type: ${type}`);
            return null;
          }
        }
      },

      [INLINES.ENTRY_HYPERLINK]: (node, children) => {
        const type = node?.data?.target?.__typename;

        switch (type) {
          case "ContentfulLcArticle": {
            const slug = node.data.target?.slug ?? "";

            return <Link to={`/learn/${slug}/`}>{children}</Link>;
          }

          case "ContentfulLcSection": {
            const section = node.data.target;

            return <Link to={`/learn/${section.slug}/`}>{children}</Link>;
          }

          case "ContentfulLcCategory": {
            const category = node.data.target;

            if (!category.lc_section) {
              console.warn("Can't render category link because 'lc_section' cannot be null. Category data:");
              console.warn(category);
              return <span>(PLACEHOLDER SEE ERROR LOG)</span>;
            }

            const section = category.lc_section[0];

            return <Link to={`/learn/${section.slug}/${category.slug}/`}>{children}</Link>;
          }

          case "ContentfulLcSubcategory": {
            const subcategory = node.data.target;

            if (!subcategory.lc_category) {
              console.warn("Can't render subcategory link because 'lc_category' cannot be null. Subcategory data:");
              console.warn(subcategory);
              return <span>(PLACEHOLDER SEE ERROR LOG)</span>;
            }

            const category = subcategory.lc_category[0];

            if (!category.lc_section) {
              console.warn(
                "Can't render subcategory link because 'lc_section' of category cannot be null. Subcategory data:"
              );
              console.warn(subcategory);
              return <span>(PLACEHOLDER SEE ERROR LOG)</span>;
            }

            const section = category.lc_section[0];

            return <Link to={`/learn/${section.slug}/${category.slug}/${subcategory.slug}/`}>{children}</Link>;
          }

          case undefined: {
            console.warn(`Undefined __typename on ENTRY_HYPERLINK`);
            return null;
          }

          default: {
            console.warn(`Unsupported hyperlink entry type: ${type}`);
            return null;
          }
        }
      },
    },
  };

  return (
    <div className={c.container}>
      {body && renderRichText(body, options)}
      {appendBlock}
    </div>
  );
}
